// components/common/LanguageSwitcher/LanguageSwitcher.tsx
import React, { useCallback, useMemo } from "react";
import Select from "@components/common/Select/Select";
import type { LanguageSwitcherProps } from "./LanguageSwitcher.props";
import { useLang } from "shared/i18n/useLang";
import { Lang } from "locales/i18n";

type Opt = { value: Lang; label: string };

type Mode = "short" | "full";

const SHORT_LABELS: Record<Lang, string> = { ru: "RU", en: "EN", tr: "TR" };

// ✅ Full names in their own language (best UX)
const FULL_LABELS: Record<Lang, string> = {
	ru: "Русский",
	en: "English",
	tr: "Türkçe",
};

const cx = (...classes: Array<string | false | undefined | null>) =>
	classes.filter(Boolean).join(" ");

const LanguageSwitcher: React.FC<LanguageSwitcherProps & { mode?: Mode }> = ({
	variant = "platform",
	mode = "short", // ✅ default: compact (RU/EN/TR) but menu can show full
	disabled = false,
	className = "",
	onChanged,
}) => {
	const { lang, setLang } = useLang();

	/**
	 * ✅ Option labels (dropdown list):
	 * - "short": show full names in dropdown (nice UX)
	 * - "full": show full names everywhere
	 */
	const optionLabels = useMemo<Record<Lang, string>>(() => FULL_LABELS, []);

	/**
	 * ✅ Closed state labels:
	 * - "short": RU/EN/TR
	 * - "full": Русский/English/Türkçe
	 */
	const closedLabels = useMemo<Record<Lang, string>>(
		() => (mode === "full" ? FULL_LABELS : SHORT_LABELS),
		[mode],
	);

	const options = useMemo<Opt[]>(
		() =>
			(["ru", "en", "tr"] as Lang[]).map((l) => ({
				value: l,
				label: optionLabels[l],
			})),
		[optionLabels],
	);

	// 👇 We override the "value" label shown in closed state based on mode
	const value = useMemo<Opt | null>(() => {
		const found = options.find((o) => o.value === lang);
		if (!found) return null;
		return { ...found, label: closedLabels[lang] };
	}, [options, lang, closedLabels]);

	const handleChange = useCallback(
		async (opt: Opt | null) => {
			if (!opt) return;
			if (opt.value === lang) return;

			await setLang(opt.value);
			onChanged?.(opt.value);
		},
		[lang, onChanged, setLang],
	);

	const selectClassName =
		variant === "landing"
			? cx("relative w-[140px] rounded-xl", className) // wider for full names
			: cx("relative w-[120px] rounded-lg", className);

	const itemClassName =
		variant === "landing"
			? cx(
					"rounded-lg px-3 py-2 text-[13px]",
					"hover:bg-light-shadeOne dark:hover:bg-dark-shadeOne",
				)
			: cx(
					"rounded-md px-3 py-2 text-[12px]",
					"hover:bg-light-grayOne dark:hover:bg-dark-grayOne",
				);

	const menuClassName =
		variant === "landing"
			? cx(
					"mt-2 w-full",
					"border border-light-strokeLanding dark:border-dark-strokeLanding",
					"bg-light-whiteText dark:bg-dark-bgColor shadow-lg",
				)
			: cx(
					"mt-2 w-full",
					"border border-light-stroke dark:border-dark-stroke",
					"bg-light-whiteText dark:bg-dark-bgColor shadow-md",
				);

	return (
		<Select
			options={options}
			value={value}
			onChange={handleChange}
			placeholder={closedLabels.ru}
			disabled={disabled}
			className={selectClassName}
			itemClassName={itemClassName}
			menuClassName={menuClassName}
		/>
	);
};

export default React.memo(LanguageSwitcher);
